import { toSnakeCase } from '../utils/caseConverter';
import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL as string;

export interface CreateBookingDraftPayload {
  productType: 'hotel' | 'flight' | 'activity' | 'package' | 'transfer' | 'other';
  provider: string;
  title?: string;
  currency?: string; // default 'INR' if omitted
  amountPaise: number; // integer
  startAt?: string | null;
  endAt?: string | null;
  expiresAt?: string | null;
  pricing?: any;
  product?: any;
  offer?: any;
  travellers?: any;
  contact?: any;
  addons?: any;
  guardianDetails?: {
    Title?: string;
    FirstName?: string;
    LastName?: string;
    PAN?: string;
  };
  extras?: any;
  providerRef?: any;
  selectedBookingCode?: string | null;
  // App associations
  queryId?: number;
  customerId?: number;
  // Ownership / responsibility
  assignedId?: number;
  createdBy?: number;
}

export const fetchBookingDrafts = async (queryId?: number) => {
  const url = `${API_BASE_URL}/bookings/draft/by-query/${String(queryId)}`;
  try {
    const res = await fetch(url, {
      headers: getApiHeaders('none'),
    });
    if (res.status === 404) return [];
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to fetch booking drafts: ${res.status} ${text}`);
    }
    const data = await res.json();
    if (Array.isArray(data)) return data;
    if (data && Array.isArray((data as any).drafts)) return (data as any).drafts;
    if (data && typeof data === 'object' && (data as any).id) return [data];
    if (data && Array.isArray((data as any).items)) return (data as any).items;
    return [];
  } catch (e) {
    // Treat network or parsing errors as no existing draft to allow create flow
    return [];
  }
};

export const extractLatestBookingDraft = async (queryId?: number) => {
  const drafts = await fetchBookingDrafts(queryId);
  if (!Array.isArray(drafts) || drafts.length === 0) return null;

  return drafts.slice().sort((a: any, b: any) => {
    const aTime = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0).getTime() || 0;
    const bTime = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0).getTime() || 0;
    return bTime - aTime;
  })[0] || null;
};

export async function createBookingDraft(payload: CreateBookingDraftPayload) {
  // Ensure association keys are present even if transform skips undefined
  const bodyObj: any = toSnakeCase(payload);
  if (typeof payload.queryId !== 'undefined') bodyObj.query_id = payload.queryId;
  if (typeof payload.customerId !== 'undefined') bodyObj.customer_id = payload.customerId;

  // (debug logging removed)

  const res = await fetch(`${API_BASE_URL}/bookings/draft`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(bodyObj),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to create booking draft: ${errorText}`);
  }
  return res.json();
}

export async function updateBookingDraft(draftId: number | string, payload: Partial<CreateBookingDraftPayload>) {
  const res = await fetch(`${API_BASE_URL}/bookings/draft/${draftId}`, {
    method: 'PUT',
    headers: getApiHeaders('json'),
    body: JSON.stringify(toSnakeCase(payload)),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to update booking draft: ${errorText}`);
  }
  return res.json();
}

export async function materializeBookingDraft(draftId: number | string) {
  const res = await fetch(`${API_BASE_URL}/bookings/${draftId}/materialize`, {
    method: 'POST',
    headers: getApiHeaders('none'),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to materialize booking: ${errorText}`);
  }
  return res.json();
}
